import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, CheckCircle2, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { projectsAPI } from '@/api/projects';
import { alternativesAPI } from '@/api/alternatives';
import { formatCurrency } from '@/utils/formatters';
import { showError, showSuccess } from '@/utils/errorHandler';
import { useAuth } from '@/context/AuthContext';
import {
  PROJECT_STATUS_OPTIONS,
  BLOCK_COLORS,
  BlockHeader,
  FieldLabel,
  MoneyInput,
  PercentInput,
  DatePickerField,
  VendorSelect,
  ManagerSelect,
  CoExecutorSelect,
  ClientSelect,
} from '@/components/project/FormHelpers';

// Константы для альтернатив
const CURRENCY_OPTIONS = [
  { value: 'KZT', label: 'KZT' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'RUB', label: 'RUB' },
  { value: 'CNY', label: 'CNY' },
];

const ALT_STATUS_OPTIONS = [
  { value: 'рассмотрение', label: 'На рассмотрении' },
  { value: 'выбрано', label: 'Выбрано' },
  { value: 'отклонено', label: 'Отклонено' },
];

const EMPTY_ALTERNATIVE = {
  vendor: '',
  solution_name: '',
  indicative_price: '',
  currency: 'KZT',
  fx_rate: '1',
  status: 'рассмотрение',
  notes: '',
};

// === MAIN COMPONENT ===

const CreateProjectDialog = ({ open, onOpenChange, onProjectCreated }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedProject, setSavedProject] = useState(null); // After save state

  // Блоки "СУБПОДРЯД И НАЛОГИ" и "ИТОГОВАЯ КАЛЬКУЛЯЦИЯ" скрыты для Senior Manager
  const userRoleLower = user?.role?.toLowerCase();
  const canViewFinanceBlocks = userRoleLower === 'admin' || userRoleLower === 'finance_admin';

  // === БЛОК 1: ПАСПОРТ ===
  const [year, setYear] = useState(new Date().getFullYear());
  const [client, setClient] = useState({ name: '', group_id: null });
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [manager, setManager] = useState({ name: '', bitrix_id: null });
  const [vendorName, setVendorName] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [customStatusMode, setCustomStatusMode] = useState(false);
  const [coExecutors, setCoExecutors] = useState([]);
  const [clientBudgetWithVat, setClientBudgetWithVat] = useState(null);
  const [deadline, setDeadline] = useState(null);  // Крайний срок

  // === БЛОК 2: ОЦЕНКА ===
  const [kpAmountWithVat, setKpAmountWithVat] = useState(null);
  const [gplWithVat, setGplWithVat] = useState(null);
  const [entryCostWithVat, setEntryCostWithVat] = useState(null);

  // === БЛОК 3: ОБЯЗАТЕЛЬСТВА ===
  const [contractAmountWithVat, setContractAmountWithVat] = useState(null);
  const [contractSignDate, setContractSignDate] = useState(null);
  const [contractCloseDate, setContractCloseDate] = useState(null);
  const [vendorPaymentPlanDate, setVendorPaymentPlanDate] = useState(null);
  const [vendorPaymentFactAmount, setVendorPaymentFactAmount] = useState(null);
  const [clientPaymentFactDate, setClientPaymentFactDate] = useState(null);

  // === БЛОК 4: ЗАТРАТЫ ===
  const [expEquipment, setExpEquipment] = useState(null);
  const [expLogistics, setExpLogistics] = useState(null);
  const [expOther, setExpOther] = useState(null);
  const [expInstallation, setExpInstallation] = useState(null);
  const [expOvertime, setExpOvertime] = useState(null);
  const [expInsurance, setExpInsurance] = useState(null);
  const [expCredit, setExpCredit] = useState(null);
  const [expPartnership, setExpPartnership] = useState(null);
  const [expPenalty, setExpPenalty] = useState(null);

  // === БЛОК 5: СУБПОДРЯД И НАЛОГИ ===
  const [subcontractPercent, setSubcontractPercent] = useState(null);
  const [subcontractAmount, setSubcontractAmount] = useState(null);
  const [taxesAmount, setTaxesAmount] = useState(null);

  // === БЛОК 6: ИТОГОВАЯ КАЛЬКУЛЯЦИЯ ===
  const [resultMargin, setResultMargin] = useState(null);
  const [resultMarginPercent, setResultMarginPercent] = useState(null);
  const [resultTaxes, setResultTaxes] = useState(null);
  const [resultGrossProfit, setResultGrossProfit] = useState(null);

  // === БЛОК 7: ПЛАН/ФАКТ (только для Finance Admin) ===
  const [budgetPlan, setBudgetPlan] = useState(null);
  const [budgetFact, setBudgetFact] = useState(null);
  const [costsFact, setCostsFact] = useState(null);

  // === БЛОК 8: АЛЬТЕРНАТИВНЫЕ РЕШЕНИЯ ===
  const [alternatives, setAlternatives] = useState([]);

  const addAlternative = () => {
    setAlternatives([...alternatives, { ...EMPTY_ALTERNATIVE, _id: Date.now() }]);
  };

  const updateAlternative = (index, field, value) => {
    setAlternatives(prev => prev.map((alt, i) =>
      i === index ? { ...alt, [field]: value } : alt
    ));
  };

  const removeAlternative = (index) => {
    setAlternatives(prev => prev.filter((_, i) => i !== index));
  };

  const calculateAltPriceKzt = (alt) => {
    const price = parseFloat(alt.indicative_price) || 0;
    const rate = parseFloat(alt.fx_rate) || 1;
    if (alt.currency === 'KZT') return price;
    return price * rate;
  };

  const resetForm = () => {
    setYear(new Date().getFullYear());
    setClient({ name: '', group_id: null });
    setProjectName('');
    setProjectDescription('');
    setManager({ name: '', bitrix_id: null });
    setVendorName('');
    setProjectStatus('');
    setCoExecutors([]);
    setClientBudgetWithVat(null);
    setDeadline(null);
    setKpAmountWithVat(null);
    setGplWithVat(null);
    setEntryCostWithVat(null);
    setContractAmountWithVat(null);
    setContractSignDate(null);
    setContractCloseDate(null);
    setVendorPaymentPlanDate(null);
    setVendorPaymentFactAmount(null);
    setClientPaymentFactDate(null);
    setExpEquipment(null);
    setExpLogistics(null);
    setExpOther(null);
    setExpInstallation(null);
    setExpOvertime(null);
    setExpInsurance(null);
    setExpCredit(null);
    setExpPartnership(null);
    setExpPenalty(null);
    setSubcontractPercent(null);
    setSubcontractAmount(null);
    setTaxesAmount(null);
    setResultMargin(null);
    setResultMarginPercent(null);
    setResultTaxes(null);
    setResultGrossProfit(null);
    setBudgetPlan(null);
    setBudgetFact(null);
    setCostsFact(null);
    setAlternatives([]);
    setError('');
    setSavedProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields (Block 1)
      if (!year) { setError('Укажите год'); setLoading(false); return; }
      if (!client.name?.trim()) { setError('Укажите заказчика'); setLoading(false); return; }
      if (!projectName.trim()) { setError('Укажите наименование проекта'); setLoading(false); return; }
      if (!projectDescription.trim()) { setError('Укажите описание проекта'); setLoading(false); return; }
      if (projectDescription.length > 3000) { setError('Описание проекта не должно превышать 3000 символов'); setLoading(false); return; }
      if (!manager.name?.trim()) { setError('Укажите менеджера'); setLoading(false); return; }
      if (!vendorName) { setError('Выберите вендора'); setLoading(false); return; }
      if (!deadline) { setError('Укажите крайний срок'); setLoading(false); return; }
      if (!projectStatus) { setError('Выберите статус проекта'); setLoading(false); return; }

      const projectData = {
        // БЛОК 1
        year: parseInt(year),
        client_name: client.name?.trim() || '',
        client_bitrix_group_id: client.group_id || null,
        name: projectName.trim(),
        description: projectDescription.trim(),
        manager: manager.name?.trim() || '',
        manager_bitrix_id: manager.bitrix_id || null,
        vendor_name: vendorName,
        supplier_name: supplierName || null,
        co_executor_ids: coExecutors.map(c => c.bitrix_id).filter(Boolean),
        co_executor_names: coExecutors.map(c => c.name),
        project_status: projectStatus,
        client_budget_with_vat: clientBudgetWithVat,
        deadline: deadline ? format(deadline, 'yyyy-MM-dd') : null,
        // БЛОК 2
        kp_amount_with_vat: kpAmountWithVat,
        gpl_project_with_vat: gplWithVat,
        entry_cost_with_vat: entryCostWithVat,
        // БЛОК 3
        contract_amount_with_vat: contractAmountWithVat,
        contract_sign_date: contractSignDate ? format(contractSignDate, 'yyyy-MM-dd') : null,
        contract_close_date: contractCloseDate ? format(contractCloseDate, 'yyyy-MM-dd') : null,
        vendor_payment_plan_date: vendorPaymentPlanDate ? format(vendorPaymentPlanDate, 'yyyy-MM-dd') : null,
        vendor_payment_fact_amount: vendorPaymentFactAmount,
        client_payment_fact_date: clientPaymentFactDate ? format(clientPaymentFactDate, 'yyyy-MM-dd') : null,
        // БЛОК 4
        expenses_equipment: expEquipment,
        expenses_logistics: expLogistics,
        expenses_other: expOther,
        expenses_installation: expInstallation,
        expenses_overtime: expOvertime,
        expenses_insurance: expInsurance,
        expenses_credit: expCredit,
        expenses_partnership: expPartnership,
        expenses_penalty: expPenalty,
        // БЛОК 5
        subcontract_percent: subcontractPercent,
        subcontract_amount: subcontractAmount,
        taxes_amount: taxesAmount,
        // БЛОК 6
        result_margin: resultMargin,
        result_margin_percent: resultMarginPercent,
        result_taxes: resultTaxes,
        result_gross_profit: resultGrossProfit,
        // БЛОК 7: ПЛАН/ФАКТ (finances)
        finances: {
          budget_plan: budgetPlan || 0,
          budget_fact: budgetFact || 0,
          costs_fact: costsFact || 0,
        },
      };

      const result = await projectsAPI.create(projectData);

      // Сохраняем альтернативы если есть
      if (alternatives.length > 0 && result.id) {
        const validAlternatives = alternatives.filter(alt => alt.vendor.trim() && alt.solution_name.trim());
        for (const alt of validAlternatives) {
          try {
            await alternativesAPI.create({
              project_id: result.id,
              vendor: alt.vendor.trim(),
              solution_name: alt.solution_name.trim(),
              indicative_price: parseFloat(alt.indicative_price) || 0,
              currency: alt.currency,
              fx_rate: parseFloat(alt.fx_rate) || 1,
              status: alt.status,
              notes: alt.notes?.trim() || null,
            });
          } catch (altErr) {
            console.warn('Failed to create alternative:', altErr);
          }
        }
      }

      setSavedProject(result);
      showSuccess('Проект успешно создан');

      if (onProjectCreated) {
        onProjectCreated();
      }
    } catch (err) {
      let errorMsg = 'Ошибка при создании проекта';
      if (err.response?.data?.detail) {
        errorMsg = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message?.includes('Network Error')) {
        errorMsg = 'Ошибка сети. Проверьте подключение.';
      }
      setError(errorMsg);
      showError(err, errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // Bitrix status display
  const bitrixId = savedProject?.bitrix_task_id;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent
        className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto"
        data-testid="create-project-dialog"
      >
        <DialogHeader>
          <DialogTitle>Новый проект</DialogTitle>
          <DialogDescription>
            Заполните данные проекта. Обязательные поля отмечены *
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-5 py-4">

            {/* ═══════════ БЛОК 1: ПАСПОРТ ═══════════ */}
            <div className="space-y-3">
              <BlockHeader title="ПАСПОРТ" color={BLOCK_COLORS.passport} />

              <div className="grid grid-cols-2 gap-3">
                {/* 1. ID Задачи (Bitrix) — read-only */}
                <div className="space-y-1">
                  <FieldLabel>ID Задачи (Bitrix)</FieldLabel>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded-md text-sm min-h-[36px]">
                    {bitrixId ? (
                      <><CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /><span>{bitrixId}</span></>
                    ) : savedProject && !bitrixId ? (
                      <><AlertTriangle className="w-4 h-4 text-yellow-600 shrink-0" /><span className="text-yellow-700">Bitrix ID не получен</span></>
                    ) : (
                      <span className="text-gray-400">— (автозаполнение)</span>
                    )}
                  </div>
                </div>

                {/* 2. Год */}
                <div className="space-y-1">
                  <FieldLabel required>Год</FieldLabel>
                  <Input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="text-center"
                    min={2020}
                    max={2035}
                    data-testid="year-input"
                  />
                </div>

                {/* 3. Наименование проекта */}
                <div className="space-y-1 col-span-2">
                  <FieldLabel required>Наименование проекта</FieldLabel>
                  <Input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Модернизация ЦОД для ТОО Генезис"
                    data-testid="project-name-input"
                  />
                </div>

                {/* 4. Описание проекта (краткое ТЗ) */}
                <div className="space-y-1 col-span-2">
                  <FieldLabel required>Описание проекта</FieldLabel>
                  <Textarea
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    placeholder="Краткое ТЗ проекта (передаётся в описание задачи Bitrix)"
                    rows={3}
                    maxLength={3000}
                    data-testid="project-description-input"
                  />
                  <div className="text-xs text-gray-500 text-right">
                    {projectDescription.length} / 3000
                  </div>
                </div>

                {/* 5. Менеджер */}
                <div className="space-y-1">
                  <FieldLabel required>Менеджер</FieldLabel>
                  <ManagerSelect
                    value={manager}
                    onChange={setManager}
                    testId="manager-input"
                  />
                </div>

                {/* Соисполнители */}
                <div className="space-y-1">
                  <FieldLabel>Соисполнители</FieldLabel>
                  <CoExecutorSelect
                    value={coExecutors}
                    onChange={setCoExecutors}
                    testId="co-executors-input"
                  />
                </div>

                {/* 6. Заказчик */}
                <div className="space-y-1">
                  <FieldLabel required>Заказчик</FieldLabel>
                  <ClientSelect
                    value={client}
                    onChange={setClient}
                    testId="client-name-input"
                  />
                </div>

                {/* Поставщик */}
                <div className="space-y-1">
                  <FieldLabel>Поставщик</FieldLabel>
                  <Input
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Название поставщика"
                  />
                </div>

                {/* 7. Статус проекта */}
                <div className="space-y-1">
                  <FieldLabel required>Статус проекта</FieldLabel>
                  {customStatusMode ? (
                    <div className="flex gap-2">
                      <Input
                        value={projectStatus}
                        onChange={(e) => setProjectStatus(e.target.value)}
                        placeholder="Введите статус вручную"
                        className="flex-1"
                        data-testid="project-status-custom"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => { setCustomStatusMode(false); setProjectStatus(''); }}
                      >
                        Список
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={projectStatus}
                      onValueChange={(v) => {
                        if (v === '__custom__') {
                          setCustomStatusMode(true);
                          setProjectStatus('');
                        } else {
                          setProjectStatus(v);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="project-status-select">
                        <SelectValue placeholder="Выберите статус" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">Другой (ввести вручную)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* 8. Бюджет от заказчика с НДС */}
                <div className="space-y-1">
                  <FieldLabel>Бюджет от заказчика с НДС</FieldLabel>
                  <MoneyInput value={clientBudgetWithVat} onChange={setClientBudgetWithVat} testId="client-budget-input" />
                </div>

                {/* 9. Вендор */}
                <div className="space-y-1">
                  <FieldLabel required>Вендор</FieldLabel>
                  <VendorSelect
                    value={vendorName}
                    onChange={setVendorName}
                    testId="vendor-select"
                  />
                </div>

                {/* 10. Крайний срок */}
                <div className="space-y-1">
                  <FieldLabel required>Крайний срок</FieldLabel>
                  <DatePickerField
                    value={deadline}
                    onChange={setDeadline}
                    testId="deadline-input"
                  />
                </div>
              </div>
            </div>

            {/* ═══════════ АЛЬТЕРНАТИВНЫЕ РЕШЕНИЯ ═══════════ */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <BlockHeader title="АЛЬТЕРНАТИВНЫЕ РЕШЕНИЯ" color={BLOCK_COLORS.alternatives} />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addAlternative}
                  className="flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Добавить
                </Button>
              </div>

              {alternatives.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm border rounded-lg bg-gray-50">
                  Нет альтернативных решений. Нажмите "Добавить" чтобы добавить.
                </div>
              ) : (
                <div className="space-y-4">
                  {alternatives.map((alt, index) => (
                    <div key={alt._id} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-700">Альтернатива #{index + 1}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeAlternative(index)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <FieldLabel required>Вендор</FieldLabel>
                          <VendorSelect
                            value={alt.vendor}
                            onChange={(v) => updateAlternative(index, 'vendor', v)}
                          />
                        </div>
                        <div className="space-y-1">
                          <FieldLabel required>Название решения</FieldLabel>
                          <Input
                            value={alt.solution_name}
                            onChange={(e) => updateAlternative(index, 'solution_name', e.target.value)}
                            placeholder="Введите название"
                          />
                        </div>
                        <div className="space-y-1">
                          <FieldLabel>Индикативная цена</FieldLabel>
                          <MoneyInput
                            value={alt.indicative_price}
                            onChange={(v) => updateAlternative(index, 'indicative_price', v)}
                          />
                        </div>
                        <div className="space-y-1">
                          <FieldLabel>Валюта</FieldLabel>
                          <Select
                            value={alt.currency}
                            onValueChange={(v) => updateAlternative(index, 'currency', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CURRENCY_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {alt.currency !== 'KZT' && (
                          <>
                            <div className="space-y-1">
                              <FieldLabel>Курс к KZT</FieldLabel>
                              <Input
                                type="number"
                                value={alt.fx_rate}
                                onChange={(e) => updateAlternative(index, 'fx_rate', e.target.value)}
                                step="0.01"
                                min="0"
                              />
                            </div>
                            <div className="space-y-1">
                              <FieldLabel>Сумма в KZT</FieldLabel>
                              <div className="px-3 py-2 bg-blue-50 border rounded-md text-blue-700 font-medium text-sm">
                                {formatCurrency(calculateAltPriceKzt(alt))}
                              </div>
                            </div>
                          </>
                        )}
                        <div className="space-y-1">
                          <FieldLabel>Статус</FieldLabel>
                          <Select
                            value={alt.status}
                            onValueChange={(v) => updateAlternative(index, 'status', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ALT_STATUS_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 col-span-2">
                          <FieldLabel>Примечания</FieldLabel>
                          <Textarea
                            value={alt.notes}
                            onChange={(e) => updateAlternative(index, 'notes', e.target.value)}
                            placeholder="Дополнительная информация"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ═══════════ БЛОК 2: ОЦЕНКА ═══════════ */}
            <div className="space-y-3">
              <BlockHeader title="ОЦЕНКА" color={BLOCK_COLORS.evaluation} />

              <div className="grid grid-cols-2 gap-3">
                {/* 8. Сумма КП с НДС */}
                <div className="space-y-1">
                  <FieldLabel>Сумма КП с НДС</FieldLabel>
                  <MoneyInput value={kpAmountWithVat} onChange={setKpAmountWithVat} testId="kp-amount-input" />
                </div>

                {/* 9. GPL проекта с НДС */}
                <div className="space-y-1">
                  <FieldLabel>GPL проекта с НДС</FieldLabel>
                  <MoneyInput value={gplWithVat} onChange={setGplWithVat} testId="gpl-input" />
                </div>

                {/* 10. Вход проекта с НДС */}
                <div className="space-y-1">
                  <FieldLabel>Вход проекта с НДС</FieldLabel>
                  <MoneyInput value={entryCostWithVat} onChange={setEntryCostWithVat} testId="entry-cost-input" />
                </div>
              </div>
            </div>

            {/* ═══════════ БЛОК 3: ОБЯЗАТЕЛЬСТВА ═══════════ */}
            <div className="space-y-3">
              <BlockHeader title="ОБЯЗАТЕЛЬСТВА" color={BLOCK_COLORS.obligations} />

              <div className="grid grid-cols-2 gap-3">
                {/* 11. Сумма договора с НДС (bold) */}
                <div className="space-y-1">
                  <FieldLabel>Сумма договора с НДС</FieldLabel>
                  <MoneyInput value={contractAmountWithVat} onChange={setContractAmountWithVat} testId="contract-amount-input" />
                </div>

                {/* 12. Дата подписания договора */}
                <div className="space-y-1">
                  <FieldLabel>Дата подписания договора</FieldLabel>
                  <DatePickerField value={contractSignDate} onChange={setContractSignDate} testId="contract-sign-date" />
                </div>

                {/* 13. Дата закрытия договора */}
                <div className="space-y-1">
                  <FieldLabel>Дата закрытия договора</FieldLabel>
                  <DatePickerField value={contractCloseDate} onChange={setContractCloseDate} testId="contract-close-date" />
                </div>

                {/* 14. Дата плановой оплаты Вендору */}
                <div className="space-y-1">
                  <FieldLabel>Дата плановой оплаты Вендору</FieldLabel>
                  <DatePickerField value={vendorPaymentPlanDate} onChange={setVendorPaymentPlanDate} testId="vendor-payment-plan-date" />
                </div>

                {/* 15. Сумма оплаты вендору (Факт) */}
                <div className="space-y-1">
                  <FieldLabel>Сумма оплаты вендору (Факт)</FieldLabel>
                  <MoneyInput value={vendorPaymentFactAmount} onChange={setVendorPaymentFactAmount} testId="vendor-payment-fact-input" />
                </div>

                {/* 16. Дата оплаты Заказчиком (Факт) */}
                <div className="space-y-1">
                  <FieldLabel>Дата оплаты Заказчиком (Факт)</FieldLabel>
                  <DatePickerField value={clientPaymentFactDate} onChange={setClientPaymentFactDate} testId="client-payment-fact-date" />
                </div>
              </div>
            </div>

            {/* ═══════════ БЛОК 4: ЗАТРАТЫ (только для Finance Admin / Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <div className="space-y-3">
                <BlockHeader title="ЗАТРАТЫ" color={BLOCK_COLORS.expenses} />

                <div className="grid grid-cols-2 gap-3">
                  {/* 17. Затраты на оборудование */}
                  <div className="space-y-1">
                    <FieldLabel>Затраты на оборудование, тг</FieldLabel>
                    <MoneyInput value={expEquipment} onChange={setExpEquipment} testId="exp-equipment-input" />
                  </div>

                  {/* 18. Затраты на логистику */}
                  <div className="space-y-1">
                    <FieldLabel>Затраты на логистику, тг</FieldLabel>
                    <MoneyInput value={expLogistics} onChange={setExpLogistics} testId="exp-logistics-input" />
                  </div>

                  {/* 19. Затраты на прочие расходы */}
                  <div className="space-y-1">
                    <FieldLabel>Затраты на прочие расходы, тг</FieldLabel>
                    <MoneyInput value={expOther} onChange={setExpOther} testId="exp-other-input" />
                  </div>

                  {/* 20. Затраты на монтаж/услуги */}
                  <div className="space-y-1">
                    <FieldLabel>Затраты на монтаж/услуги, тг</FieldLabel>
                    <MoneyInput value={expInstallation} onChange={setExpInstallation} testId="exp-installation-input" />
                  </div>

                  {/* 21. Переработки */}
                  <div className="space-y-1">
                    <FieldLabel>Переработки, тг</FieldLabel>
                    <MoneyInput value={expOvertime} onChange={setExpOvertime} testId="exp-overtime-input" />
                  </div>

                  {/* 22. Страхование */}
                  <div className="space-y-1">
                    <FieldLabel>Страхование</FieldLabel>
                    <MoneyInput value={expInsurance} onChange={setExpInsurance} testId="exp-insurance-input" />
                  </div>

                  {/* 23. Кредит */}
                  <div className="space-y-1">
                    <FieldLabel>Кредит</FieldLabel>
                    <MoneyInput value={expCredit} onChange={setExpCredit} testId="exp-credit-input" />
                  </div>

                  {/* 24. % за партнерство, тг */}
                  <div className="space-y-1">
                    <FieldLabel>% за партнерство, тг</FieldLabel>
                    <MoneyInput value={expPartnership} onChange={setExpPartnership} testId="exp-partnership-input" />
                  </div>

                  {/* 25. Пеня */}
                  <div className="space-y-1">
                    <FieldLabel>Пеня, тг</FieldLabel>
                    <MoneyInput value={expPenalty} onChange={setExpPenalty} testId="exp-penalty-input" />
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ БЛОК 5: СУБПОДРЯД И НАЛОГИ (только для Finance Admin / Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <div className="space-y-3">
                <BlockHeader title="СУБПОДРЯД И НАЛОГИ" color={BLOCK_COLORS.subcontract} />

                <div className="grid grid-cols-2 gap-3">
                  {/* 26. % субподряда */}
                  <div className="space-y-1">
                    <FieldLabel>% субподряда</FieldLabel>
                    <PercentInput value={subcontractPercent} onChange={setSubcontractPercent} testId="subcontract-percent-input" />
                  </div>

                  {/* 27. Сумма (субподряда) */}
                  <div className="space-y-1">
                    <FieldLabel>Сумма (субподряда)</FieldLabel>
                    <MoneyInput value={subcontractAmount} onChange={setSubcontractAmount} testId="subcontract-amount-input" />
                  </div>

                  {/* 28. Налоги */}
                  <div className="space-y-1">
                    <FieldLabel>Налоги</FieldLabel>
                    <MoneyInput value={taxesAmount} onChange={setTaxesAmount} testId="taxes-amount-input" />
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ БЛОК 6: ИТОГОВАЯ КАЛЬКУЛЯЦИЯ (только для Finance Admin / Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <div className="space-y-3">
                <BlockHeader title="ИТОГОВАЯ КАЛЬКУЛЯЦИЯ" color={BLOCK_COLORS.total} />

                <div className="grid grid-cols-2 gap-3">
                  {/* 29. Маржа, тг */}
                  <div className="space-y-1">
                    <FieldLabel>Маржа, тг</FieldLabel>
                    <MoneyInput value={resultMargin} onChange={setResultMargin} testId="result-margin-input" />
                  </div>

                  {/* 30. Маржа, % */}
                  <div className="space-y-1">
                    <FieldLabel>Маржа, %</FieldLabel>
                    <PercentInput value={resultMarginPercent} onChange={setResultMarginPercent} testId="result-margin-percent-input" />
                  </div>

                  {/* 31. Налоги, тг */}
                  <div className="space-y-1">
                    <FieldLabel>Налоги, тг</FieldLabel>
                    <MoneyInput value={resultTaxes} onChange={setResultTaxes} testId="result-taxes-input" />
                  </div>

                  {/* 32. Валовая прибыль, тг */}
                  <div className="space-y-1">
                    <FieldLabel>Валовая прибыль, тг</FieldLabel>
                    <MoneyInput value={resultGrossProfit} onChange={setResultGrossProfit} testId="result-gross-profit-input" />
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ БЛОК 7: ПЛАН/ФАКТ (только для Finance Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <div className="space-y-3">
                <BlockHeader title="ПЛАН / ФАКТ" color={BLOCK_COLORS.total} />

                <div className="grid grid-cols-3 gap-3">
                  {/* Плановый бюджет */}
                  <div className="space-y-1">
                    <FieldLabel>План (бюджет), тг</FieldLabel>
                    <MoneyInput value={budgetPlan} onChange={setBudgetPlan} testId="budget-plan-input" />
                  </div>

                  {/* Фактически получено */}
                  <div className="space-y-1">
                    <FieldLabel>Факт (получено), тг</FieldLabel>
                    <MoneyInput value={budgetFact} onChange={setBudgetFact} testId="budget-fact-input" />
                  </div>

                  {/* Фактические затраты */}
                  <div className="space-y-1">
                    <FieldLabel>Затраты (факт), тг</FieldLabel>
                    <MoneyInput value={costsFact} onChange={setCostsFact} testId="costs-fact-input" />
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-md text-sm" data-testid="error-message">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Success message after save */}
            {savedProject && (
              <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-md text-sm">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Проект создан{alternatives.length > 0 ? ` с ${alternatives.length} альтернативами` : ''}. {bitrixId ? `Bitrix ID: ${bitrixId}` : 'Bitrix ID не получен — можно привязать позже.'}
              </div>
            )}
          </div>

          <DialogFooter>
            {savedProject ? (
              <Button type="button" onClick={handleClose}>
                Закрыть
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-project-btn"
                >
                  {loading ? 'Сохранение...' : 'Сохранить проект'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectDialog;
